import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  getCycleDetailAPI,
  getEmployeeResponseAPI,
  createOrUpdateAppraisalResponseAPI
} from "api/apiHelper";
import { AvailableCycle, AppraisalResponse, CreateAppraisalResponseData } from "types/appraisal-response-types";
import Card from "components/Card";
import Button from "components/Button";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import { toast } from "react-toastify";
import TextField from "components/TextField";

const RATING_SCALE = [5, 4, 3, 2, 1];
const RATING_LABELS: { [key: number]: string } = {
  5: "Excellent",
  4: "Very Good",
  3: "Good",
  2: "Fair",
  1: "Poor"
};

const FillAppraisal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cycleId, employeeId } = useParams<{ cycleId: string; employeeId: string }>();

  // --- State Management ---
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cycle, setCycle] = useState<any | null>(null);
  const [existingResponse, setExistingResponse] = useState<AppraisalResponse | null>(null);
  const [questionResponses, setQuestionResponses] = useState<{ [key: number]: { rating: number; comment: string } }>({});

  const isViewRoute = location.pathname.includes('/response/');
  const isValidFillRoute = cycleId && employeeId && !isViewRoute && location.pathname.includes('/employee/');

  useEffect(() => {
    if (!isValidFillRoute) return;
    loadData();
  }, [cycleId, employeeId, isValidFillRoute]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [cycleRes, existingRes] = await Promise.allSettled([
        getCycleDetailAPI(Number(cycleId)),
        getEmployeeResponseAPI(Number(cycleId), Number(employeeId))
      ]);

      if (cycleRes.status === 'fulfilled') {
        const cycleData = cycleRes.value.data as any;
        setCycle(cycleData);

        let existing: AppraisalResponse | null = null;
        if (existingRes.status === 'fulfilled') {
          const d: any = existingRes.value.data;
          existing = d?.data?.id ? d.data : (d?.id ? d : null);
          setExistingResponse(existing);
        }

        // Clients typically use the 'client_review_template' or the generic 'template'
        const activeTemplate = cycleData.client_review_template || cycleData.template;

        if (activeTemplate?.questions) {
          const map: any = {};
          activeTemplate.questions.forEach((q: any) => {
            map[q.id] = { rating: 0, comment: '' };
          });

          existing?.question_responses?.forEach((qr: any) => {
            map[qr.question_id] = { rating: qr.rating, comment: qr.comment || '' };
          });
          setQuestionResponses(map);
        }
      } else {
        throw cycleRes.reason;
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Failed to load appraisal");
      toast.error("Failed to load appraisal");
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = (id: number, r: number) => {
    setQuestionResponses(p => ({ ...p, [id]: { ...p[id], rating: r } }));
  };

  const handleCommentChange = (id: number, c: string) => {
    setQuestionResponses(p => ({ ...p, [id]: { ...p[id], comment: c } }));
  };

  const handleSubmit = async () => {
    if (!cycle || !employeeId) return;

    const activeTemplate = cycle.client_review_template || cycle.template;

    if (activeTemplate.questions.some((q: any) => !questionResponses[q.id]?.rating)) {
      toast.error("Please answer all evaluation criteria before submitting");
      return;
    }

    try {
      setSubmitting(true);
      const payload: any = {
        cycle_id: cycle.id,
        employee_id: Number(employeeId),
        reviewer_type: 'CLIENT',
        question_responses: activeTemplate.questions.map((q: any) => ({
          question_id: q.id,
          rating: questionResponses[q.id]?.rating || 1,
          comment: questionResponses[q.id]?.comment || ''
        })),
        status: 'SUBMITTED'
      };

      await createOrUpdateAppraisalResponseAPI(payload);
      toast.success("Client feedback submitted successfully");
      navigate('/client-appraisals');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d: string) => {
    if (!d) return "N/A";
    return new Date(d).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const isDeadlinePassed = cycle ? new Date(cycle.deadline) < new Date() : false;
  const isReadOnly = isDeadlinePassed;

  const activeTemplate = cycle?.client_review_template || cycle?.template;

  const totalScore = activeTemplate?.questions?.reduce((s: number, q: any) => {
    const r = questionResponses[q.id]?.rating || 0;
    return s + (r * (q.weightage || 0)) / 100;
  }, 0) || 0;

  if (loading) return <Backdrop show />;
  if (error) return <div className="mt-5 px-4"><ErrorBox errorMessage={error} /></div>;
  if (!activeTemplate) return <div className="mt-5 px-4"><ErrorBox errorMessage="Template not found" /></div>;

  const employee = cycle?.participants?.find((p: any) => p.id === Number(employeeId));

  return (
    <div className="mt-5 max-w-6xl mx-auto px-4">
      {/* HEADER CARD */}
      <Card extra="p-6 border-t-8 border-t-brand-500 mb-6 shadow-sm">
        <h2 className="text-3xl font-bold text-navy-700 dark:text-white mb-2">{cycle.name}</h2>
        <div className="flex flex-col sm:flex-row sm:gap-8 pt-4 border-t border-gray-100 dark:border-navy-700">
          <div>
            <p className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Evaluating Professional</p>
            <p className="font-semibold text-lg text-brand-500">{employee?.full_name || `ID: ${employeeId}`}</p>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Reviewer Role</p>
            <p className="font-semibold text-lg uppercase text-navy-700 dark:text-white">Client Feedback</p>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Submission Deadline</p>
            <p className={`font-semibold text-lg ${isDeadlinePassed ? 'text-red-500' : 'text-navy-700 dark:text-white'}`}>
              {formatDate(cycle.deadline)}
            </p>
          </div>
        </div>
      </Card>

      {/* SCORE & PROGRESS PREVIEW */}
      <Card extra="p-5 mb-6 border border-brand-100 bg-brand-50/30 dark:bg-brand-900/10 shadow-sm">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-xs font-bold text-brand-600 mb-1 uppercase tracking-widest">Calculated Satisfaction Score</p>
            <p className="text-4xl font-bold text-brand-700 dark:text-brand-300">
              {totalScore.toFixed(2)} <span className="text-lg font-medium text-gray-400">/ 5.00</span>
            </p>
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-xs text-gray-400 font-semibold mb-1 uppercase tracking-widest">Form Status</p>
            <p className="text-xl font-bold text-navy-700 dark:text-white">
              {Object.values(questionResponses).filter(v => v.rating > 0).length} / {activeTemplate.questions.length} Fields Done
            </p>
          </div>
        </div>
      </Card>

      {/* FORM TABLE PANEL */}
      <div className="relative bg-white dark:bg-navy-800 rounded-xl shadow-sm border dark:border-navy-700 overflow-hidden">
        {/* STICKY TABLE HEADER */}
        <div className="sticky top-0 z-20 bg-gray-50 dark:bg-navy-900 border-b dark:border-navy-700 shadow-sm">
          <div className="grid grid-cols-12 gap-4 p-5">
            <div className="col-span-12 md:col-span-6 font-bold text-sm text-gray-500 uppercase tracking-widest">Feedback Area</div>
            <div className="hidden md:grid col-span-6 grid-cols-5 text-center">
              {RATING_SCALE.map(n => (
                <div key={n} className="flex flex-col items-center">
                  <p className="font-bold text-navy-700 dark:text-white text-base">{n}</p>
                  <p className="text-[9px] text-gray-400 uppercase font-bold tracking-tighter">{RATING_LABELS[n]}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* QUESTIONS LIST */}
        <div className="divide-y dark:divide-navy-700">
          {activeTemplate.questions.map((q: any, i: number) => {
            const selected = questionResponses[q.id]?.rating;
            return (
              <div key={q.id} className="p-6 transition-all hover:bg-gray-50/50 dark:hover:bg-navy-700/40">
                <div className="grid grid-cols-12 gap-6">
                  {/* Question Text */}
                  <div className="col-span-12 md:col-span-6">
                    <div className="flex gap-4">
                      <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-sm font-bold bg-brand-50 text-brand-600 dark:bg-brand-900/20 dark:text-brand-400 rounded-lg">
                        {i + 1}
                      </span>
                      <div>
                        <h3 className="text-base font-semibold text-navy-700 dark:text-white leading-tight">
                          {q.question_text} <span className="text-red-500 ml-1">*</span>
                        </h3>
                        <p className="text-[11px] text-gray-400 mt-2 font-medium uppercase tracking-wide">Category Influence: {q.weightage}%</p>
                      </div>
                    </div>
                  </div>

                  {/* Rating Input Group */}
                  <div className="col-span-12 md:col-span-6 grid grid-cols-5 gap-2 items-center">
                    {RATING_SCALE.map(r => (
                      <label key={r} className="flex flex-col items-center cursor-pointer group">
                        <input
                          type="radio"
                          name={`q-${q.id}`}
                          className="hidden"
                          checked={selected === r}
                          disabled={isReadOnly}
                          onChange={() => handleRatingChange(q.id, r)}
                        />
                        <div className={`
                          w-11 h-11 rounded-xl border-2 flex items-center justify-center transition-all duration-200
                          ${selected === r 
                            ? 'bg-brand-500 border-brand-500 text-white shadow-lg scale-105' 
                            : 'border-gray-100 dark:border-navy-600 text-gray-300 group-hover:border-brand-200'}
                        `}>
                          <span className="font-extrabold text-lg">{r}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Qualitative Feedback */}
                <div className="mt-6 md:pl-12">
                  <TextField
                    id={`comment-${q.id}`}
                    placeholder="Provide optional qualitative details for our records..."
                    value={questionResponses[q.id]?.comment || ''}
                    onChange={(e: any) => handleCommentChange(q.id, e.target.value)}
                    disabled={isReadOnly}
                    extra="w-full bg-gray-50/30 dark:bg-navy-900/20"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* FOOTER ACTIONS */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-8 mb-16 px-2">
        <Button 
          varient="secondary" 
          onClick={() => navigate('/client-appraisals')} 
          className="w-full sm:w-auto px-10 border-2"
        >
          Cancel Feedback
        </Button>

        {!isReadOnly && (
          <div className="flex gap-4 w-full sm:w-auto">
            <Button 
                onClick={() => {
                    if(window.confirm("Do you want to reset your entries?")) {
                        setQuestionResponses(prev => {
                            const reset: any = {};
                            Object.keys(prev).forEach(key => reset[key] = {rating: 0, comment: ''});
                            return reset;
                        });
                    }
                }}
                className="flex-1 sm:flex-none px-6 text-gray-500"
            >
              Reset
            </Button>
            <Button 
              varient="primary" 
              disabled={submitting} 
              onClick={handleSubmit}
              className="flex-1 sm:flex-none px-14 shadow-xl shadow-brand-200 dark:shadow-none"
            >
              {submitting ? 'Submitting...' : 'Submit Evaluation'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FillAppraisal;